import React, { useState, useEffect } from 'react';
import { dateToLocal, formatMoney, handleErrorToast } from '../../shared/Functions';
import { $BillCreate, BillingAccountsVService, TransactionsVService, $BillUpdate, BillsVService, AllowedAction } from '../../../services/V2/gozer';
import { TabbedTable } from '../../shared/TabbedTable';
import { authProvider } from '../../../services/Security';
import { AddModal } from '../../shared/AddModal';
import { EditModal } from '../../shared/EditModal';
import { Bill } from '../Bill';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ReceiptIcon from '@material-ui/icons/Receipt';
import BackspaceIcon from '@material-ui/icons/Backspace';
import RestoreIcon from '@material-ui/icons/Restore';
import { PaymentModal } from './tokens/PaymentModal';

export const Ledger = ({ loading, setLoading, billingAccountId, style, successCallback, failureCallback }) => {
    const [canUpdateBill, setCanUpdateBill] = useState<boolean>(false);
    const [canCreateBill, setCanCreateBill] = useState<boolean>(false);
    const [canDeleteBill, setCanDeleteBill] = useState<boolean>(false);
    const [canMakePayment, setCanMakePayment] = useState<boolean>(false);
    const [canVoidTransaction, setCanVoidTransaction] = useState<boolean>(false);
    const [canRefundTransaction, setCanRefundTransaction] = useState<boolean>(false);
    const [rowData, setRowData] = useState<any>();
    const [showAddBill, setShowAddBill] = useState<boolean>(false);
    const [showEditBill, setShowEditBill] = useState<boolean>(false);
    const [showBill, setShowBill] = useState<boolean>(false);
    const [showOneTimePayment, setShowOneTimePayment] = useState<boolean>(false);

    const tabColumns = [
        [
            { title: 'Billing Cycle', field: 'billingCycle', editable: 'never' },
            { title: 'Billing Date', field: 'billDate', editable: 'never', render: rowData => dateToLocal(rowData.billDate).toLocaleString() },
            { title: 'Due Date', field: 'dueDate', editable: 'never', render: rowData => dateToLocal(rowData.dueDate).toLocaleString() },
            { title: 'Past Due Amount', field: 'pastDueAmount', editable: 'never', render: rowData => formatMoney(rowData.pastDueAmount) },
            { title: 'Due Amount', field: 'dueAmount', editable: 'never', render: rowData => formatMoney(rowData.dueAmount) }
        ],
        [
            { title: 'Date', field: 'createdAt', editable: 'never', render: rowData => dateToLocal(rowData.createdAt).toLocaleString() },
            { title: 'Amount', field: 'amount', editable: 'never', render: rowData => formatMoney(rowData.amount) },
            { title: 'Service Fee', field: 'serviceFeeAmount', editable: 'never', render: rowData => formatMoney(rowData.serviceFeeAmount) },
            { title: 'Account', field: 'paymentType', editable: 'never', render: rowData => `${rowData.paymentType} ${rowData.token?.last4 ?? "????"}` },
            { title: 'Approval Code', field: 'approvalCode', editable: 'never' },
            { title: 'Type', field: 'paymentTransactionType', editable: 'never' }
        ]
    ];
    const tabDetails = [
        [],
        []
    ]
    const tabActions = [
        [
            {
                icon: () => <AddIcon />,
                tooltip: 'Add Bill',
                isFreeAction: true,
                onClick: () => setShowAddBill(true),
                disabled: !canCreateBill
            },
            {
                icon: () => <EditIcon />,
                tooltip: 'Edit Bill',
                onClick: (event, row) => handleShowEditBill(row),
                disabled: !canUpdateBill
            },
            {
                icon: () => <VisibilityIcon />,
                tooltip: 'View Bill',
                onClick: (event, row) => handleShowBill(row)
            }
        ],
        [
            {
                icon: () => <ReceiptIcon />,
                tooltip: 'Make One Time Payment',
                isFreeAction: true,
                onClick: () => setShowOneTimePayment(true),
                disabled: !canMakePayment
            },
            rowData => {
                return {
                    icon: () => <BackspaceIcon />,
                    tooltip: 'Void Payment',
                    onClick: (event, row) => handleRefundVoid(TransactionsVService.transactionsVVoid, row, "void"),
                    disabled: !canVoidTransaction || rowData.allowedAction !== AllowedAction.VOID
            }},
            rowData => {
                return {
                    icon: () => <RestoreIcon />,
                    tooltip: 'Refund Payment',
                    onClick: (event, row) => handleRefundVoid(TransactionsVService.transactionsVRefund, row, "refund"),
                    disabled: !canRefundTransaction || rowData.allowedAction !== AllowedAction.REFUND
            }}
        ]
    ]

    const tabData = [
        query => {
            return new Promise((resolve, reject) => {
                BillsVService.billsVGetAll(billingAccountId, {
                    pageNumber: (query?.page ?? 0) + 1,
                    numberPerPage: query?.pageSize ?? 5,
                    orderBy: query?.orderBy?.field?.toString() ?? "DueDate",
                    orderByDesc: query?.orderDirection === "desc",
                    filters: []
                }).then((x:any) => {
                    setLoading(false)
                    resolve({
                        data: x.items ?? [],
                        page: query?.page ?? 0,
                        totalCount: x.numberOfItems ?? 0
                    })
                }).catch(x => {
                    reject(x)
                })
            })
        },
        query => {
            return new Promise((resolve, reject) => {
                TransactionsVService.transactionsVGetAll(billingAccountId, {
                    pageNumber: (query?.page ?? 0) + 1,
                    numberPerPage: query?.pageSize ?? 5,
                    orderBy: query?.orderBy?.field?.toString() ?? "CreatedAt",
                    orderByDesc: (query?.orderDirection ?? "desc") === "desc",
                    filters: [{
                        property: "BillingAccountId",
                        value: billingAccountId
                    }]
                }).then((x:any) => {
                    setLoading(false)
                    resolve({
                        data: x.items ?? [],
                        page: query?.page ?? 0,
                        totalCount: x.numberOfItems ?? 0
                    })
                }).catch(x => {
                    reject(x)
                })
            })
        }
    ];

    useEffect(() => {
        let perms = authProvider.getPermissions();
        setCanUpdateBill(perms.some((perm: string) => perm === "UpdateBills"));
        setCanCreateBill(perms.some((perm: string) => perm === "CreateBills"));
        setCanDeleteBill(perms.some((perm: string) => perm === "DeleteBills"));
        setCanMakePayment(perms.some((perm: string) => perm === "ChargeTransactions"));
        setCanVoidTransaction(perms.some((perm: string) => perm === "VoidTransactions"));
        setCanRefundTransaction(perms.some((perm: string) => perm === "RefundTransactions"));
    }, [])

    const handleRefundVoid = (endpoint, row, verbage) => {
        let confirm = window.confirm(`Are you sure you want to ${verbage}?\nYou are going to ${verbage} payment of ${formatMoney(row.amount)} made on ${dateToLocal(row.createdAt).toLocaleDateString()} from account: ${row.account} with approval code: ${row.approvalCode}.`);
        if(confirm){
            endpoint(billingAccountId, row.id, {
                    amount: row.amount,
                    emailAddress: row.ccToken && row.ccToken.emailAddress !== "" ? row.ccToken.emailAddress :
                        row.achToken && row.achToken.emailAddress !== "" ? row.achToken.emailAddress : null
                })
                .then(v => {
                    successCallback(v.resultDetail)
                    setLoading(!loading);
                })
                .catch(err => failureCallback(handleErrorToast(err)))
        }
}

    const handleShowEditBill = (row) => {
        setRowData(row)
        setShowEditBill(true);
    }

    const handleShowBill = (row) => {
        setRowData(row)
        setShowBill(true);
    }

    return (
        <>
            {showAddBill &&
                <AddModal
                    setShow={setShowAddBill}
                    show={showAddBill}
                    setLoading={setLoading}
                    loading={loading}
                    addEndpoint={BillingAccountsVService.billingAccountsVCreate}
                    keys={$BillCreate.properties}
                    modelName="Bill"
                    parentId={billingAccountId}
                    parentsParentId={null}
                    parent={null}
                    parentValues={[]}
                    canReadUserRoles={false}
                    successMessage={null}
                    successCallback={null}
                    canReadMeters={false}
                    canReadServiceAddresses={false}
                    districtId={null}
                />}
            {showEditBill &&
                <EditModal
                    modelId={rowData.id}
                    parentId={billingAccountId}
                    parentsParentId={null}
                    data={rowData}
                    modelName="Bill"
                    setShow={setShowEditBill}
                    setLoading={setLoading}
                    loading={loading}
                    isEdit={true}
                    editEndpoint={BillingAccountsVService.billingAccountsVUpdateBillingAccount}
                    extraEndpoint={null}
                    deleteEndpoint={BillingAccountsVService.billingAccountsVUpdateBillingAccount}
                    viewKeys={null}
                    editKeys={$BillUpdate.properties}
                    extraKeys={null}
                    hardDelete={canDeleteBill}
                    canReadUserRoles={false}
                    canReadMeters={false}
                    canReadServiceAddresses={false}
                    districtId={null}
                />}
            {showBill &&
                <Bill bill={rowData} billingAccountId={billingAccountId} setOpen={setShowBill} />
            }
            {showOneTimePayment &&
                <PaymentModal
                    setShow={setShowOneTimePayment}
                    billingAccountId={billingAccountId}
                    successCallback={successCallback}
                    failureCallback={failureCallback}
                    loading={loading}
                    setLoading={setLoading} />
            }
            <TabbedTable
                tabData={tabData}
                tabColumns={tabColumns}
                tabDetails={tabDetails}
                tabActions={tabActions}
                tabNames={["Bills", "Transactions"]}
                style={style}
                />
        </>
    );
}